import { REMOTE_ENTRY_MODULE, isRemoteRegistered, loadRemoteApp } from '@host/mf'
import { Alert, Button, Container, ErrorBoundary, Skeleton, Text } from '@prismeet/react'
import { useCallback, useEffect, useState, type ComponentType } from 'react'

import { MESSAGES, SKELETON_ROWS } from './constants'
import styles from './index.module.scss'
import type { RemoteOutletProps, RemoteOutletState } from './types'

interface LoadState {
  /** Что именно грузили: имя ремоута, модуль и номер попытки. */
  key: string
  status: 'loading' | 'ready' | 'failed'
  Component: ComponentType | null
}

/**
 * Точка монтирования микрофронта.
 *
 * Микрофронты живут в других репозиториях и деплоятся независимо, поэтому
 * их отсутствие — не аварийная ситуация, а рабочее состояние: на чистой
 * машине разработчика хост поднимается с нулём ремоутов и обязан выглядеть
 * осмысленно. Отсюда три разных исхода вместо одного «ошибка»:
 *
 *   не зарегистрирован — адрес неизвестен, показываем как его добавить;
 *   не загрузился      — адрес есть, сборка не ответила, даём «Повторить»;
 *   упал при отрисовке — ошибка внутри чужого кода, ловится границей,
 *                        остальная оболочка продолжает работать.
 */
export function RemoteOutlet({
  name,
  module = REMOTE_ENTRY_MODULE,
  placeholder,
}: RemoteOutletProps) {
  const [attempt, setAttempt] = useState(0)
  const requestKey = `${name}|${module}|${String(attempt)}`

  const [load, setLoad] = useState<LoadState>(() => ({
    key: requestKey,
    status: 'loading',
    Component: null,
  }))

  // Сброс при смене ремоута делается во время рендера, а не в эффекте:
  // из эффекта он вызвал бы лишний кадр со старым компонентом на новом
  // маршруте — то есть чужой раздел, мелькнувший перед нужным.
  if (load.key !== requestKey) {
    setLoad({ key: requestKey, status: 'loading', Component: null })
  }

  // Регистрация — синхронная проверка модульного реестра, так что состояние
  // «ремоут неизвестен» вычисляется, а не хранится.
  const registered = isRemoteRegistered(name)

  useEffect(() => {
    if (!registered) return

    let cancelled = false

    loadRemoteApp(name, module)
      .then((Component) => {
        if (cancelled) return
        // Функция в setState трактуется как ленивый инициализатор, поэтому
        // компонент кладётся обёрнутым — иначе React вызовет его как обычную
        // функцию и положит в состояние результат рендера.
        setLoad({ key: requestKey, status: 'ready', Component: () => <Component /> })
      })
      .catch(() => {
        if (!cancelled) setLoad({ key: requestKey, status: 'failed', Component: null })
      })

    return () => {
      cancelled = true
    }
  }, [name, module, registered, requestKey])

  const retry = useCallback(() => {
    setAttempt((value) => value + 1)
  }, [])

  const state: RemoteOutletState = registered ? load.status : 'unregistered'

  if (state === 'unregistered') {
    if (placeholder) return placeholder

    return (
      <Container
        surface="glass"
        glassLevel={1}
        radius="2xl"
        padding={5}
        border="subtle"
        className={styles.notice}
      >
        <Text as="h2" variant="title">
          {MESSAGES.unregisteredTitle}
        </Text>
        <Text variant="body" tone="secondary">
          {MESSAGES.unregisteredDescription}
        </Text>
        <code className={styles.hint}>
          {`VITE_REMOTES=${name}@http://localhost:5174/remoteEntry.js`}
        </code>
      </Container>
    )
  }

  // Заглушка закрывает и загрузку, и падение: сайдбар и прочие элементы
  // оболочки живут в узких колонках, куда карточки-скелетоны и Alert
  // по центру просто не помещаются. Оболочке нужен работающий фолбэк.
  if (state === 'failed') {
    if (placeholder) return placeholder

    return (
      <Alert
        status="danger"
        title={MESSAGES.failedTitle}
        action={
          <Button size="sm" variant="soft" onClick={retry}>
            {MESSAGES.retry}
          </Button>
        }
      >
        {MESSAGES.failedDescription}
      </Alert>
    )
  }

  if (state === 'loading' || !load.Component) {
    if (placeholder) return placeholder

    return (
      <div className={styles.skeleton} aria-busy="true" aria-live="polite">
        <span className="visually-hidden">Загружается раздел</span>
        {Array.from({ length: SKELETON_ROWS }, (_, index) => (
          <Container key={index} surface="raised" radius="2xl" className={styles.card}>
            <div className={styles.head}>
              <Skeleton variant="circle" size={44} />
              <Skeleton variant="block" width={160} height={14} radius="full" />
            </div>
            <Skeleton variant="text" lines={3} />
          </Container>
        ))}
      </div>
    )
  }

  const RemoteApp = load.Component

  return (
    <ErrorBoundary
      // Смена ремоута обязана сбрасывать границу: иначе переход
      // в исправный раздел показывал бы заглушку от упавшего соседа.
      resetKeys={[requestKey]}
      title={MESSAGES.crashedTitle}
      description={MESSAGES.crashedDescription}
      retryLabel={MESSAGES.retry}
      showDetails={import.meta.env.DEV}
    >
      <RemoteApp />
    </ErrorBoundary>
  )
}
